import React, { useEffect, useState } from 'react';

const AnimatedAvatar = ({ isMoving, isOpeningDoor }) => {
  const [walkFrame, setWalkFrame] = useState(0);
  const [armAngle, setArmAngle] = useState(0);
  const [blinkState, setBlinkState] = useState(false);
  const [breatheOffset, setBreatheOffset] = useState(0);

  // Constant breathing animation
  useEffect(() => {
    const breatheInterval = setInterval(() => {
      setBreatheOffset(prev => (prev + 1) % 60);
    }, 50);
    return () => clearInterval(breatheInterval);
  }, []);

  // Blinking animation
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setBlinkState(true);
      setTimeout(() => setBlinkState(false), 150);
    }, 3000 + Math.random() * 2000);
    return () => clearInterval(blinkInterval);
  }, []);

  // Walking animation
  useEffect(() => {
    if (isMoving) {
      const interval = setInterval(() => {
        setWalkFrame(prev => (prev + 1) % 8);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isMoving]);

  // Door opening arm animation
  useEffect(() => {
    if (isOpeningDoor) {
      const sequence = [0, 15, 30, 45, 60, 45, 30, 15, 0];
      let index = 0;
      const interval = setInterval(() => {
        setArmAngle(sequence[index]);
        index++;
        if (index >= sequence.length) {
          clearInterval(interval);
          setArmAngle(0);
        }
      }, 120);
      return () => clearInterval(interval);
    }
  }, [isOpeningDoor]);

  // Calculate animation offsets
  const legOffset = isMoving ? Math.sin(walkFrame * 0.8) * 12 : 0;
  const armSwing = isMoving ? Math.sin(walkFrame * 0.8) * 15 : Math.sin(breatheOffset * 0.1) * 3;
  const bodyBounce = isMoving ? Math.abs(Math.sin(walkFrame * 0.8)) * 3 : Math.sin(breatheOffset * 0.1) * 2;
  const headTilt = Math.sin(breatheOffset * 0.05) * 2;

  return (
    <svg
      viewBox="0 0 140 200"
      className="animated-avatar-svg"
    >
      {/* Shadow on ground */}
      <ellipse 
        cx="70" 
        cy="195" 
        rx={isMoving ? 28 : 25 + Math.sin(breatheOffset * 0.1) * 2} 
        ry="5" 
        fill="rgba(0,0,0,0.2)"
      />
      
      {/* Body group with bounce */}
      <g style={{ transform: `translateY(${-bodyBounce}px)` }}>
        
        {/* Legs */}
        <g>
          {/* Left leg */}
          <g style={{ 
            transform: `rotate(${legOffset}deg)`, 
            transformOrigin: '55px 130px',
          }}>
            <rect x="48" y="130" width="16" height="45" rx="6" fill="#2D3748" />
            <ellipse cx="56" cy="175" rx="12" ry="7" fill="#1A202C" />
            <ellipse cx="60" cy="174" rx="4" ry="3" fill="#374151" />
          </g>
          
          {/* Right leg */}
          <g style={{ 
            transform: `rotate(${-legOffset}deg)`, 
            transformOrigin: '85px 130px',
          }}>
            <rect x="76" y="130" width="16" height="45" rx="6" fill="#2D3748" />
            <ellipse cx="84" cy="175" rx="12" ry="7" fill="#1A202C" />
            <ellipse cx="88" cy="174" rx="4" ry="3" fill="#374151" />
          </g>
        </g>

        {/* Body - Polo shirt */}
        <path
          d="M40 78 L45 135 L95 135 L100 78 L88 68 L70 74 L52 68 Z"
          fill="#3D2B1F"
        />
        
        {/* Collar with stripes */}
        <path d="M52 68 L70 78 L88 68" stroke="#DC2626" strokeWidth="3" fill="none" />
        <path d="M53 70 L70 79 L87 70" stroke="#FFFFFF" strokeWidth="2" fill="none" />
        <path d="M54 72 L70 80 L86 72" stroke="#2563EB" strokeWidth="2" fill="none" />
        
        {/* Shirt buttons */}
        <circle cx="70" cy="92" r="2.5" fill="#E5E5E5" />
        <circle cx="70" cy="105" r="2.5" fill="#E5E5E5" />
        <circle cx="70" cy="118" r="2.5" fill="#E5E5E5" />
        
        {/* Left arm */}
        <g style={{ 
          transform: `rotate(${-armSwing}deg)`, 
          transformOrigin: '40px 78px',
        }}>
          <rect x="28" y="78" width="14" height="40" rx="7" fill="#3D2B1F" />
          <ellipse cx="35" cy="120" rx="9" ry="10" fill="#8B6914" />
          <ellipse cx="32" cy="126" rx="3" ry="4" fill="#7A5A10" />
          <ellipse cx="38" cy="126" rx="3" ry="4" fill="#7A5A10" />
        </g>
        
        {/* Right arm - can reach for door */}
        <g style={{ 
          transform: `rotate(${isOpeningDoor ? -armAngle : armSwing}deg)`, 
          transformOrigin: '100px 78px',
        }}>
          <rect x="98" y="78" width="14" height="40" rx="7" fill="#3D2B1F" />
          <ellipse cx="105" cy="120" rx="9" ry="10" fill="#8B6914" />
          <g style={{
            transform: isOpeningDoor ? 'translateX(5px)' : 'none',
          }}>
            <ellipse cx="102" cy="126" rx="3" ry="4" fill="#7A5A10" />
            <ellipse cx="108" cy="126" rx="3" ry="4" fill="#7A5A10" />
          </g>
        </g>

        {/* Head with slight tilt */}
        <g style={{ transform: `rotate(${headTilt}deg)`, transformOrigin: '70px 42px' }}>
          <ellipse cx="70" cy="42" rx="32" ry="36" fill="#8B6914" />
          
          {/* Hair */}
          <ellipse cx="70" cy="18" rx="30" ry="18" fill="#1A1A1A" />
          <ellipse cx="40" cy="32" rx="10" ry="14" fill="#1A1A1A" />
          <ellipse cx="100" cy="32" rx="10" ry="14" fill="#1A1A1A" />
          <path d="M50 15 Q55 10, 60 14" stroke="#2D2D2D" strokeWidth="2" fill="none" />
          <path d="M65 12 Q70 8, 75 12" stroke="#2D2D2D" strokeWidth="2" fill="none" />
          <path d="M80 15 Q85 10, 90 14" stroke="#2D2D2D" strokeWidth="2" fill="none" />
          
          {/* Ears */}
          <ellipse cx="38" cy="45" rx="6" ry="10" fill="#8B6914" />
          <ellipse cx="38" cy="45" rx="3" ry="6" fill="#7A5A10" />
          <ellipse cx="102" cy="45" rx="6" ry="10" fill="#8B6914" />
          <ellipse cx="102" cy="45" rx="3" ry="6" fill="#7A5A10" />
          
          {/* Eyes */}
          {!blinkState ? (
            <>
              <ellipse cx="55" cy="42" rx="8" ry="5" fill="#FFFFFF" />
              <ellipse cx="85" cy="42" rx="8" ry="5" fill="#FFFFFF" />
              <circle cx={55 + (isMoving ? 2 : Math.sin(breatheOffset * 0.05) * 1)} cy="42" r="4" fill="#2D1810" />
              <circle cx={85 + (isMoving ? 2 : Math.sin(breatheOffset * 0.05) * 1)} cy="42" r="4" fill="#2D1810" />
              <circle cx={56 + (isMoving ? 2 : 0)} cy="40" r="1.5" fill="#FFFFFF" />
              <circle cx={86 + (isMoving ? 2 : 0)} cy="40" r="1.5" fill="#FFFFFF" />
            </>
          ) : (
            <>
              <path d="M47 42 Q55 45, 63 42" stroke="#1A1A1A" strokeWidth="2" fill="none" />
              <path d="M77 42 Q85 45, 93 42" stroke="#1A1A1A" strokeWidth="2" fill="none" />
            </>
          )}
          
          {/* Glasses */}
          <rect x="43" y="35" width="22" height="16" rx="4" fill="none" stroke="#1A1A1A" strokeWidth="3" />
          <rect x="75" y="35" width="22" height="16" rx="4" fill="none" stroke="#1A1A1A" strokeWidth="3" />
          <path d="M65 42 L75 42" stroke="#1A1A1A" strokeWidth="2.5" />
          <path d="M43 40 L38 38" stroke="#1A1A1A" strokeWidth="2.5" />
          <path d="M97 40 L102 38" stroke="#1A1A1A" strokeWidth="2.5" />
          <path d="M48 38 L53 43" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
          <path d="M80 38 L85 43" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
          
          {/* Eyebrows */}
          <path d="M45 30 Q55 27, 63 31" stroke="#1A1A1A" strokeWidth="2.5" fill="none" />
          <path d="M77 31 Q85 27, 95 30" stroke="#1A1A1A" strokeWidth="2.5" fill="none" />
          
          {/* Nose */}
          <ellipse cx="70" cy="54" rx="8" ry="6" fill="#7A5A10" opacity="0.6" />
          <ellipse cx="66" cy="56" rx="3" ry="2" fill="#6B4F0E" opacity="0.4" />
          <ellipse cx="74" cy="56" rx="3" ry="2" fill="#6B4F0E" opacity="0.4" />
          
          {/* Mouth - animated smile */}
          <path 
            d={`M56 66 Q70 ${72 + Math.sin(breatheOffset * 0.1) * 2}, 84 66`} 
            stroke="#5D3A1A" 
            strokeWidth="2.5" 
            fill="none" 
          />
          
          {/* Chin stubble */}
          <ellipse cx="70" cy="72" rx="12" ry="4" fill="#5D4A1A" opacity="0.15" />
        </g>
      </g>
    </svg>
  );
};

export default AnimatedAvatar;
